import { useEffect, useRef, useState } from "react";

/**
 * Loadout <Reveal>: opacity 0→1 + 12px translate-up over 480ms ease-out.
 * Pure IntersectionObserver + CSS transitions, no motion library.
 * Stagger siblings with the `delay` prop (in ms) — spec calls for 60ms between siblings.
 */
export default function Reveal({
  children,
  delay = 0,
  y = 12,
  duration = 480,
  className = "",
  as: Tag = "div",
  once = true,
  amount = 0.15,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) io.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: amount },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, amount]);

  const style = {
    transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
    transitionDelay: `${delay}ms`,
    transform: visible ? "translate3d(0,0,0)" : `translate3d(0,${y}px,0)`,
    opacity: visible ? 1 : 0,
    willChange: "opacity, transform",
  };

  return (
    <Tag ref={ref} style={style} className={className}>
      {children}
    </Tag>
  );
}
